/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import axios from 'axios';

import { toTitleCase, asyncDebounce } from 'sarsaparilla';
import { getAuthHeader } from 'sarsaparilla/auth';

import type {
    Suggestion,
    ContentSuggestion,
    InventorySuggestion,
    LocationSuggestion,
} from './LocationSuggestion.types';

import {
    isContentSuggestion,
    isHistorySuggestion,
    isInventorySuggestion,
} from './LocationSuggestion.utils';

export const TEXT_SEARCH_NEAR_ME = 'Search for places or activities near me';
export const TEXT_CLEAR_SEARCH_HIST = 'Clear Search History';

// get search suggestion string to display in the drop down
export function getSuggestionDisplayValue(suggestion: Suggestion): string {
    let suggestionText = suggestion.text;
    if (
        (isContentSuggestion(suggestion) ||
            isInventorySuggestion(suggestion) ||
            isHistorySuggestion(suggestion)) &&
        suggestion.entity_id &&
        suggestion.entity_type
    ) {
        // if suggestion is associated with an inventory name, then capitalize the name to clean it up
        suggestionText = toTitleCase(suggestionText);
    }

    return suggestionText;
}

export function containsEmail(email: unknown) {
    // the regex below triggers a security error `Polynomial regular expression used on uncontrolled data`
    // To prevent a long-running regex from causing a DoS attack, we check that the email length isn't crazy long
    if (!email || typeof email !== 'string' || email.length > 500) {
        return null;
    }

    return email?.match(
        // eslint-disable-next-line no-useless-escape
        /(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))/
    );
}

type SearchSuggestionResult = {
    content_suggestions: null | ContentSuggestion[];
    inventory_suggestions: null | InventorySuggestion[];
    suggestions: null | LocationSuggestion[];
};

export const getSearchSuggestions = asyncDebounce(
    async (
        signal: AbortSignal,
        params: {
            q: string;
            geocoder: boolean;
            type: string[] | null;
            inventory_type: string[] | null;
        }
    ) => {
        try {
            const { data } = await axios.get<SearchSuggestionResult>(
                '/api/search/suggest',
                {
                    signal,
                    params,
                    paramsSerializer: {
                        indexes: null,
                    },
                    headers: { Authorization: getAuthHeader() },
                }
            );

            return data;
        } catch (error) {
            // eat error
            return {
                content_suggestions: null,
                inventory_suggestions: null,
                suggestions: null,
            };
        }
    },
    250
);
